import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/api/schema';
import { formatUZS } from '@/lib/format';
import { generateContractPDF } from '@/lib/contract-pdf';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ArrowLeft, User, CreditCard, MapPin, Calendar, Hash, Clock, AlertCircle, Printer, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { dateLocale } from '@/lib/locale';
import { checkMurabahaConsistency } from '@/lib/murabaha-consistency';
import { useAuth } from '@/context/AuthContext';
import { InvoiceCard } from '@/components/invoices/InvoiceCard';
import { FiscalReceiptPanel } from '@/components/fiscal/FiscalReceiptPanel';
import { BankContractBadge } from '@/components/murabaha/BankContractBadge';

type MurabahaCase = components['schemas']['MurabahaCaseResource'];
type Contract = components['schemas']['ContractResource'];
type Customer = components['schemas']['CustomerResource'];
type SaleItem = components['schemas']['SaleItemResource'];

const statusColors: Record<string, string> = {
  Draft: 'bg-muted text-muted-foreground',
  PendingCustomerConfirmation: 'bg-warning/10 text-warning',
  Approved: 'bg-info/10 text-info',
  Rejected: 'bg-destructive/10 text-destructive',
  Completed: 'bg-success/10 text-success',
  Cancelled: 'bg-muted text-muted-foreground',
};

const paymentStatusColors: Record<string, string> = {
  none: 'bg-muted text-muted-foreground',
  in_progress: 'bg-warning/10 text-warning',
  cancelled: 'bg-destructive/10 text-destructive',
  paid: 'bg-success/10 text-success',
};

function InfoRow({ icon: Icon, label, value, className }: { icon?: React.ComponentType<{ size?: number; className?: string }>; label: string; value: React.ReactNode; className?: string }) {
  return (
    <div className={`flex items-start gap-3 py-2 ${className || ''}`}>
      {Icon && <Icon size={15} className="text-muted-foreground mt-0.5 shrink-0" />}
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm text-foreground break-all">{value || '—'}</p>
      </div>
    </div>
  );
}

function ContractCard({ contract, customer, saleItems, createdAt }: { contract: Contract; customer: Customer | null | undefined; saleItems: SaleItem[]; createdAt: string | null }) {
  const { t } = useTranslation();
  const handlePrint = () => {
    generateContractPDF({
      contract: {
        contract_number: contract.contract_number,
        total_amount: Number(contract.total_amount),
        monthly_payment: Number(contract.monthly_payment),
        term_months: contract.term_months,
        created_at: createdAt ?? new Date().toISOString(),
        status: contract.status,
      },
      customer: customer ? {
        full_name: customer.full_name,
        phone: customer.phone,
        passport: customer.passport,
        pinfl: customer.pinfl,
      } : null,
      schedule: (contract.payment_schedule ?? []).map(row => ({ n: row.n, due_date: row.due_date, amount: Number(row.amount), remaining: row.remaining })),
      saleItems: saleItems.map((item) => ({
        product_name: item.product_name,
        quantity: Number(item.quantity),
        unit_price: Number(item.unit_price),
        total: Number(item.total),
        unit: item.unit,
        mhik: item.mhik || '',
      })),
    });
  };

  return (
    <div className="border border-border rounded-lg p-3 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground font-mono">{contract.contract_number || <span className="text-warning font-sans">{t('mur_detail.contract_id_waiting')}</span>}</span>
        <div className="flex items-center gap-1.5">
          <BankContractBadge status={contract.bank_status} labels="mur_detail.bank_status" />
          <Badge className={`text-xs border-0 ${contract.status === 'signed' ? 'bg-success/10 text-success' : contract.status === 'cancelled' ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground'}`}>
            {t(`mur_detail.contract_status.${contract.status}`, { defaultValue: contract.status })}
          </Badge>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div><span className="text-muted-foreground">{t('mur_detail.sum')}</span> <span className="text-foreground">{formatUZS(Number(contract.total_amount))}</span></div>
        <div><span className="text-muted-foreground">{t('mur_detail.monthly')}</span> <span className="text-foreground">{formatUZS(Number(contract.monthly_payment))}</span></div>
        <div><span className="text-muted-foreground">{t('mur_detail.term')}</span> <span className="text-foreground">{t('mur_detail.months', { n: contract.term_months })}</span></div>
      </div>
      <p className="text-xs text-muted-foreground">{t('mur_detail.created')} {new Date(contract.created_at).toLocaleDateString(dateLocale())}</p>
      <div className="flex gap-2 pt-1">
        <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={handlePrint}>
          <Printer size={14} /> {t('mur_detail.print_pdf')}
        </Button>
      </div>
    </div>
  );
}

export default function MurabahaDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'overview';
  const setTab = (v: string) => setSearchParams(p => { p.set('tab', v); return p; }, { replace: true });

  const caseId = id ? Number(id) : null;

  const { data: mCase, isLoading } = useQuery({
    queryKey: ['/murabaha/cases/{case}', caseId],
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/murabaha/cases/{case}', { params: { path: { case: caseId! } } });
      if (error) throw error;
      return data?.data as MurabahaCase;
    },
    enabled: !!caseId,
  });

  if (isLoading) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <p className="text-muted-foreground text-center py-12">{t('mur_detail.loading')}</p>
      </div>
    );
  }

  if (!mCase) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="text-center py-12">
          <AlertCircle className="mx-auto mb-3 text-muted-foreground" size={32} />
          <p className="text-muted-foreground">{t('mur_detail.not_found')}</p>
          <Link to="/murabaha" className="text-primary text-sm mt-2 inline-block hover:underline">{t('mur_detail.back_to_list')}</Link>
        </div>
      </div>
    );
  }

  const customer = mCase.customer;
  const branch = mCase.branch;
  const sale = mCase.sale;
  const contract = mCase.contract;
  const orders = mCase.order ?? [];
  const invStatus = mCase.invoice?.status ?? null;

  // Shartnoma summasi = tovar narxi + ustama (mijoz Hisobga to'laydi)
  // Do'konga to'lanadi = faqat tovar narxi (buyurtma / savdo summasi)
  const contractTotal = Number(contract?.total_amount ?? mCase.total_amount ?? 0);
  const goodsTotal = Number(orders[0]?.total_amount ?? sale?.total_amount ?? 0);
  const payoutAmount = goodsTotal > 0 ? goodsTotal : contractTotal;
  const markupAmount = Math.max(0, contractTotal - payoutAmount);

  // Yagona Shartnoma ID — Loan Managerdan keladi (murabaha va shartnoma uchun bir xil)
  const unifiedContractId = mCase.contract_number || contract?.contract_number || null;

  // Ish holati va shartnoma holati mosligini avtomatik tekshirish
  const consistency = checkMurabahaConsistency(
    { status: mCase.status, payment_status: mCase.payment_status, contract_id: mCase.contract_number ?? null },
    contract ? { status: contract.status, contract_number: contract.contract_number } : null
  );

  const saleItems = sale?.items ?? [];
  const limitExpired = customer?.limit_expires_at ? new Date(customer.limit_expires_at) < new Date() : false;

  const fmtDT = (d: string | null | undefined) => d ? `${new Date(d).toLocaleDateString(dateLocale())} ${new Date(d).toLocaleTimeString(dateLocale(), { hour: '2-digit', minute: '2-digit' })}` : '—';
  const copy = (v: string) => { navigator.clipboard?.writeText(v); toast.success(t('murabaha_detail.copied')); };

  const steps = [
    { key: 'sale', done: mCase.order_id != null || orders.length > 0 },
    { key: 'contract', done: contract?.status === 'signed' || ['Approved', 'Completed'].includes(mCase.status), active: !!contract },
    { key: 'invoice', done: invStatus === 'approved', active: !!invStatus && invStatus !== 'approved', error: invStatus === 'error' || invStatus === 'rejected' },
    { key: 'approval', done: ['Approved', 'Completed'].includes(mCase.status), error: ['Rejected', 'Cancelled'].includes(mCase.status) },
    { key: 'payment', done: mCase.payment_status === 'paid', active: mCase.payment_status === 'in_progress', error: mCase.payment_status === 'cancelled' },
  ];

  const empty = () => <p className="text-sm text-muted-foreground text-center py-10">{t('invoice.no_data')}</p>;

  return (
    <div className="w-full min-w-0 px-2 py-4 sm:px-3 md:px-0 md:py-6 animate-fade-in space-y-5">
      {/* Header */}
      <div className="bg-card border border-border rounded-2xl p-5">
        <div className="flex items-start gap-3 flex-wrap">
          <Link to="/murabaha" className="mt-1 text-muted-foreground hover:text-foreground transition-colors" aria-label={t('murabaha_detail.back')}>
            <ArrowLeft size={20} />
          </Link>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground">{t('mur_detail.case')}</p>
            <div className="flex items-center gap-2 flex-wrap">
              {unifiedContractId ? (
                <>
                  <h1 className="text-xl md:text-2xl font-semibold font-mono text-foreground tracking-tight">{unifiedContractId}</h1>
                  <button onClick={() => copy(unifiedContractId)} className="text-muted-foreground hover:text-foreground" aria-label={t('murabaha_detail.copy')}><Copy size={15} /></button>
                </>
              ) : (
                <h1 className="text-lg font-semibold text-warning">{t('murabaha_detail.contract_id_pending')}</h1>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-1">
              <span className="flex items-center gap-1"><User size={12} />{customer?.full_name || '—'}</span>
              <span className="flex items-center gap-1"><MapPin size={12} />{branch?.name || '—'}</span>
              <span className="flex items-center gap-1"><Calendar size={12} />{fmtDT(mCase.created_at)}</span>
              <span className="flex items-center gap-1"><Clock size={12} />{t('murabaha_detail.updated')}: {fmtDT(mCase.updated_at)}</span>
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs">
              <span className="text-muted-foreground">{t('murabaha_detail.limit')}: <span className="font-medium text-foreground">{customer?.murabaha_limit ? formatUZS(Number(customer.murabaha_limit)) : '—'}</span></span>
              <span className="text-muted-foreground">{t('murabaha_detail.limit_expires')}: <span className={`font-medium ${limitExpired ? 'text-destructive' : 'text-foreground'}`}>{customer?.limit_expires_at ? `${new Date(customer.limit_expires_at).toLocaleDateString(dateLocale())}${limitExpired ? ` (${t('murabaha_detail.expired')})` : ''}` : '—'}</span></span>
              <span className="text-muted-foreground">{t('murabaha_detail.purchases')}: <span className="font-medium text-foreground">{customer?.total_purchases ?? 0} / {formatUZS(Number(customer?.total_spent ?? 0))}</span></span>
              {customer?.id && <Link to={`/customers/${customer.id}`} className="text-primary font-medium hover:underline">{t('murabaha_detail.open_customer')} →</Link>}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            <Badge className={`text-xs border-0 ${statusColors[mCase.status] || ''}`}>{t(`mur_status.${mCase.status}`, { defaultValue: mCase.status })}</Badge>
            <Badge className={`text-xs border-0 ${paymentStatusColors[mCase.payment_status] || ''}`}>{t(`mur_detail.pay.${mCase.payment_status}`, { defaultValue: mCase.payment_status })}</Badge>
          </div>
        </div>

        {/* Progress steps */}
        <ol className="mt-5 grid grid-cols-5 gap-2">
          {steps.map((s, i) => {
            const tone = s.error ? 'bg-destructive' : s.done ? 'bg-success' : s.active ? 'bg-warning' : 'bg-muted';
            const text = s.error ? 'text-destructive' : s.done ? 'text-foreground' : s.active ? 'text-warning' : 'text-muted-foreground';
            return (
              <li key={s.key} className="space-y-1.5">
                <div className={`h-1.5 rounded-full ${tone}`} />
                <p className={`text-[11px] font-medium ${text}`}>{i + 1}. {t(`murabaha_detail.steps.${s.key}`)}</p>
              </li>
            );
          })}
        </ol>
      </div>

      {!consistency.ok && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3">
          <p className="text-sm font-semibold text-destructive flex items-center gap-2 mb-1">
            <AlertCircle size={15} /> {t('mur_detail.mismatch', { count: consistency.issues.length })}
          </p>
          <ul className="space-y-0.5 text-xs text-foreground">
            {consistency.issues.map(i => (<li key={i.code}>• <span className="font-medium">{i.label}:</span> {i.detail}</li>))}
          </ul>
        </div>
      )}

      {/* Key numbers */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { k: 'contract_total', v: formatUZS(contractTotal), c: 'text-foreground' },
          { k: 'payout', v: formatUZS(payoutAmount), c: 'text-success' },
          { k: 'markup', v: formatUZS(markupAmount), c: 'text-foreground' },
          { k: 'limit_amount', v: formatUZS(Number(mCase.limit_amount)), c: 'text-foreground' },
        ].map(x => (
          <div key={x.k} className="bg-card border border-border rounded-xl p-4">
            <p className="text-xs text-muted-foreground">{t(`murabaha_detail.kpi.${x.k}.label`)}</p>
            <p className={`text-lg font-semibold mt-1 ${x.c}`}>{x.v}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">{t(`murabaha_detail.kpi.${x.k}.hint`)}</p>
          </div>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="min-w-0">
        <TabsList className="w-full justify-start overflow-x-auto h-auto flex-wrap">
          <TabsTrigger value="overview">{t('murabaha_detail.tabs.overview')}</TabsTrigger>
          <TabsTrigger value="items">{t('murabaha_detail.tabs.items')} <span className="ml-1 text-muted-foreground">{saleItems.length}</span></TabsTrigger>
          <TabsTrigger value="invoice" className="gap-1.5">
            {t('murabaha_detail.tabs.invoice')} {invStatus && <span className={`h-2 w-2 rounded-full ${invStatus === 'approved' ? 'bg-success' : invStatus === 'error' || invStatus === 'rejected' ? 'bg-destructive' : 'bg-warning'}`} />}
          </TabsTrigger>
          <TabsTrigger value="docs">{t('murabaha_detail.tabs.docs')} <span className="ml-1 text-muted-foreground">{contract ? 1 : 0}</span></TabsTrigger>
          <TabsTrigger value="orders">{t('murabaha_detail.tabs.orders')} <span className="ml-1 text-muted-foreground">{orders.length}</span></TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <FiscalReceiptPanel caseId={mCase.id} canRetry={user?.role === 'owner' || user?.role === 'manager'} customerName={customer?.full_name} customerPhone={customer?.phone} />
          <div className="bg-card border border-border rounded-xl p-5 divide-y divide-border">
            <InfoRow icon={Hash} label={t('mur_detail.contract_id')} value={unifiedContractId ? <span className="font-mono text-xs">{unifiedContractId}</span> : <span className="text-xs text-warning">{t('mur_detail.pending_lm')}</span>} />
            <InfoRow label={t('mur_detail.contract_status_label')} value={<Badge className={`text-xs border-0 ${statusColors[mCase.status] || ''}`}>{t(`mur_status.${mCase.status}`, { defaultValue: mCase.status })}</Badge>} />
            <InfoRow icon={CreditCard} label={t('mur_detail.payment_status_label')} value={
              <span className="flex flex-col items-end gap-0.5">
                <Badge className={`text-xs border-0 ${paymentStatusColors[mCase.payment_status] || ''}`}>{t(`mur_detail.pay.${mCase.payment_status}`, { defaultValue: mCase.payment_status })}</Badge>
                <span className="text-[10px] text-muted-foreground">{t('mur_detail.auto_lm')}</span>
              </span>
            } />
            <InfoRow label={t('mur_detail.payout_label')} value={<span className="font-medium text-success">{formatUZS(payoutAmount)}</span>} />
            <InfoRow label={t('mur_detail.limit')} value={formatUZS(Number(mCase.limit_amount))} />
            <InfoRow icon={MapPin} label={t('mur_detail.branch')} value={branch?.name ?? mCase.branch_id} />
            <InfoRow icon={Calendar} label={t('mur_detail.created_date')} value={fmtDT(mCase.created_at)} />
            <InfoRow icon={Clock} label={t('murabaha_detail.updated')} value={fmtDT(mCase.updated_at)} />
            {[
              { l: t('mur_detail.case_no'), v: mCase.id },
              { l: t('mur_detail.cashier_id'), v: mCase.cashier_id },
              { l: t('mur_detail.sale_id'), v: mCase.sale_id },
              { l: t('mur_detail.order_id'), v: mCase.order_id },
            ].filter(x => x.v != null).map(x => (
              <InfoRow key={x.l} label={x.l} value={
                <span className="flex items-center gap-1.5">
                  <span className="font-mono text-xs">{x.v}</span>
                  <button onClick={() => copy(String(x.v))} className="text-muted-foreground hover:text-foreground" aria-label={t('murabaha_detail.copy')}><Copy size={11} /></button>
                </span>
              } />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="items">
          <div className="bg-card border border-border rounded-xl p-2">
            {saleItems.length === 0 ? empty() : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left text-xs text-muted-foreground p-3 font-medium">{t('mur_detail.col_product')}</th>
                      <th className="text-left text-xs text-muted-foreground p-3 font-medium">MHIK</th>
                      <th className="text-right text-xs text-muted-foreground p-3 font-medium">{t('mur_detail.col_qty')}</th>
                      <th className="text-left text-xs text-muted-foreground p-3 font-medium">{t('mur_detail.col_unit')}</th>
                      <th className="text-right text-xs text-muted-foreground p-3 font-medium">{t('mur_detail.col_price')}</th>
                      <th className="text-right text-xs text-muted-foreground p-3 font-medium">{t('mur_detail.col_total')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {saleItems.map((item) => (
                      <tr key={item.id} className="border-b border-border last:border-0">
                        <td className="p-3 text-foreground">{item.product_name}</td>
                        <td className="p-3 text-muted-foreground font-mono text-xs">{item.mhik || '—'}</td>
                        <td className="p-3 text-right text-foreground">{item.quantity}</td>
                        <td className="p-3 text-muted-foreground">{item.unit}</td>
                        <td className="p-3 text-right text-foreground">{formatUZS(Number(item.unit_price))}</td>
                        <td className="p-3 text-right font-medium text-foreground">{formatUZS(Number(item.total))}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-border">
                      <td colSpan={5} className="p-3 text-right text-sm font-medium text-muted-foreground">{t('mur_detail.total')}</td>
                      <td className="p-3 text-right font-semibold text-foreground">{formatUZS(Number(sale?.total_amount ?? mCase.total_amount))}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="invoice">
          <InvoiceCard caseId={mCase.id} canSign={user?.role === 'owner'} canRetry={user?.role === 'owner' || user?.role === 'manager'} />
        </TabsContent>

        <TabsContent value="docs">
          <div className="bg-card border border-border rounded-xl p-4 space-y-3">
            {contract ? <ContractCard contract={contract} customer={customer} saleItems={saleItems} createdAt={mCase.created_at} /> : empty()}
          </div>
        </TabsContent>

        <TabsContent value="orders">
          <div className="bg-card border border-border rounded-xl p-4 space-y-3">
            {orders.length === 0 ? empty() : orders.map((o) => (
              <div key={o.id} className="border border-border rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">#{o.order_number}</span>
                  <Badge className={`text-xs border-0 ${o.status === 'confirmed' ? 'bg-success/10 text-success' : o.status === 'cancelled' ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground'}`}>
                    {t(`mur_detail.order_status.${o.status}`, { defaultValue: o.status })}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{t('mur_detail.sum')} {formatUZS(Number(o.total_amount))} • {new Date(o.created_at).toLocaleDateString(dateLocale())}</p>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
